import React, { createContext, useContext, useMemo, useCallback, useState } from 'react';
import { FeatureFlagService } from './FeatureFlagService';
import { FeatureFlagContextType, FeatureFlagId, FeatureFlag } from './types';

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

interface FeatureFlagProviderProps {
  children: React.ReactNode;
  userId?: string;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children, userId }) => {
  const [service] = useState(() => new FeatureFlagService(userId));
  const [, forceUpdate] = useState({});

  const isEnabled = useCallback((flagId: FeatureFlagId): boolean => {
    return service.isEnabled(flagId);
  }, [service]);

  const getFlag = useCallback((flagId: FeatureFlagId): FeatureFlag | null => {
    return service.getFlag(flagId);
  }, [service]);

  const getAllFlags = useCallback((): FeatureFlag[] => {
    return service.getAllFlags();
  }, [service]);

  const setFlag = useCallback((flagId: FeatureFlagId, enabled: boolean): void => {
    service.setFlag(flagId, enabled);
    forceUpdate({});
  }, [service]);

  const setRolloutPercentage = useCallback((flagId: FeatureFlagId, percentage: number): void => {
    service.setRolloutPercentage(flagId, percentage);
    forceUpdate({});
  }, [service]);

  const reload = useCallback((): void => {
    service.reload();
    forceUpdate({});
  }, [service]);

  const value = useMemo<FeatureFlagContextType>(() => ({
    isEnabled,
    getFlag,
    getAllFlags,
    setFlag,
    setRolloutPercentage,
    reload,
  }), [isEnabled, getFlag, getAllFlags, setFlag, setRolloutPercentage, reload]);

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlag = (flagId: FeatureFlagId): boolean => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    console.warn('useFeatureFlag used outside FeatureFlagProvider, returning false');
    return false;
  }
  return context.isEnabled(flagId);
};

export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
  }
  return context;
};
